import { uniqBy, sortBy, filter } from "lodash";
import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import bind from "memoize-bind";
import memoize from "memoize-weak";

import CSSModules from "lib/utils/css-modules";
import { SubBar, MainView, SlotsCalendar, FormControl, Dropdown, Menu } from "lib/components/index";
import { open as openShowSlotModal } from "modules/calendar/ShowSlotModal";
import { stateSelector, initializeView, fetchSlots, setFilters } from "./index";
import styles from "./CalendarView.less";
import { st, ProductsRepository } from "lib/index";

const calendarModifiers = {
  past: (event) => moment(event.starts_at).isBefore(),
  active: (event) => event.active,
  inactive: (event) => !event.active,
  awaitingCompany: (event) => event.awaiting_for_company,
  awaitingCustomer: (event) => event.awaiting_for_customer,
  selected: (event) => event.booked,
};

const filterScopeOptions = [
  ["all", I18n.t("papp.calendar.CalendarView.filter_all")],
  ["available", I18n.t("papp.calendar.CalendarView.filter_available")],
  ["awaiting", I18n.t("papp.calendar.CalendarView.filter_awaiting")],
  ["booked", I18n.t("papp.calendar.CalendarView.filter_booked")],
];

const getStartDate = () => {
  if (st.authorize.company.reseller) {
    return moment().format(moment.RUBY_DATE_FORMAT);
  } else {
    return moment().startOf("month").add(-7, "days").format(moment.RUBY_DATE_FORMAT);
  }
};

const getEndDate = () => {
  if (st.authorize.company.reseller) {
    return moment().add(7, "days").format(moment.RUBY_DATE_FORMAT);
  } else {
    return moment().endOf("month").add(7, "days").format(moment.RUBY_DATE_FORMAT);
  }
};

@connect(
  (state) => ({
    currentCompany: state.auth.company.model,
    currentProducts: state.auth.products.model || [],
    allSlots: stateSelector(state).slots.model,
    filteredSlots: stateSelector(state).filteredSlots,
    fetchDone: stateSelector(state).slots.fetchDone,
    fetchLoading: stateSelector(state).slots.fetchLoading,
    fetchFailed: stateSelector(state).slots.fetchFailed,
    filters: stateSelector(state).filters,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        openShowSlotModal,
        initializeView,
        fetchSlots,
        setFilters,
      },
      dispatch
    )
)
@CSSModules(styles)
export default class CalendarView extends React.PureComponent {
  componentDidMount() {
    this.props.initializeView({
      filters: {
        scope: "all",
        supplier_id: this._calculateInitialSupplierId(),
        product_id: null,
        starts_at_gte: getStartDate(),
        starts_at_lte: getEndDate(),
        hide_cutofftime: !st.authorize.company.supplier,
        show_inactive_offers: false,
      },
    });

    const supplierId = this._calculateInitialSupplierId();
    this.getProducts({ supplier_id: supplierId, limit: 100 });
  }

  componentDidUpdate(prevProps) {
    const prevSupplierId = prevProps.filters && prevProps.filters.supplier_id;
    const nextSupplierId = this.props.filters && this.props.filters.supplier_id;
    if (prevSupplierId !== nextSupplierId) {
      this.getProducts({ supplier_id: nextSupplierId, limit: 100 });
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions.length = 0;
  }

  handleSlotSelect(slot) {
    this.props.openShowSlotModal(slot);
  }

  handleFilterChange(prop, value) {
    this.props.setFilters({ ...this.props.filters, [prop]: value });
  }

  handleCalendarNavigate({ currentDate, minVisibleDate, maxVisibleDate }) {
    this.props.setFilters({
      ...this.props.filters,
      starts_at_gte: minVisibleDate,
      starts_at_lte: maxVisibleDate,
      hide_cutofftime: !st.authorize.company.supplier,
    });
  }

  subscriptions = [];
  userProducts = [];
  _lastFetchedSupplierId = undefined;

  getProducts(params) {
    if (this._lastFetchedSupplierId === params.supplier_id) return;
    this._lastFetchedSupplierId = params.supplier_id;

    const sub = ProductsRepository.search(params).subscribe((products) => {
      this.userProducts = products.records;
      this.forceUpdate();
    });
    this.subscriptions.push(sub);
  }

  _calculateInitialSupplierId() {
    const { currentCompany } = this.props;
    if (currentCompany.supplier) {
      return currentCompany.id;
    }
    if (currentCompany.agent) {
      const suppliers = currentCompany.getPossibleSuppliers();
      return suppliers ? suppliers[0].id : null;
    }
    return null;
  }

  filterCurrentProducts(currentProducts = [], supplier_id) {
    const subscribedAndCurrentProducts = uniqBy([...this.userProducts, ...currentProducts], (p) => p.id);

    if (!subscribedAndCurrentProducts) {
      return [];
    }

    if (!supplier_id) {
      return subscribedAndCurrentProducts;
    }

    return filter(
      subscribedAndCurrentProducts,
      (product) => product.booking_type === "static_date_booking" && product.company.id === supplier_id
    );
  }

  render() {
    const {
      currentCompany,
      currentProducts,
      fetchLoading,
      fetchFailed,
      fetchDone,
      fetchSlots,
      allSlots,
      filteredSlots,
      filters,
      setFilters,
    } = this.props;

    return (
      <MainView.Container>
        <SubBar>
          <div className="u-inlineFlexRow">
            <h1>{I18n.t("papp.calendar.CalendarView.title")}</h1>

            {currentCompany.agent && (
              <FormControl.Select
                value={filters.supplier_id}
                onChange={bind(this.handleFilterChange, this, "supplier_id")}
                options={currentCompany.getPossibleSuppliers().map((c) => [c.id, c.name])}
                placeholder={I18n.t("papp.calendar.CalendarView.all_suppliers")}
                className="c-inputStandard u-marginLeft u-largeWidth"
                search
              />
            )}

            <FormControl.ProductsSelect
              products={this.filterCurrentProducts(currentProducts.records, filters.supplier_id)}
              value={filters.product_id}
              onChange={bind(this.handleFilterChange, this, "product_id")}
              placeholder={I18n.t("papp.calendar.CalendarView.all_products")}
              fluid
              className="c-inputStandard u-marginHorizontal u-largeWidth"
            />

            <Menu secondary pointing className="u-marginNone">
              {filterScopeOptions.map(([scope, label]) => (
                <Menu.Item
                  key={scope}
                  active={filters.scope === scope}
                  onClick={bind(this.handleFilterChange, this, "scope", scope)}
                  content={label}
                />
              ))}
            </Menu>

            <FormControl.Checkbox
              label={I18n.t("papp.calendar.CalendarView.show_inactive_offers")}
              value={filters.show_inactive_offers}
              onChange={bind(this.handleFilterChange, this, "show_inactive_offers")}
              toggle
              className="u-marginLeft"
            />
          </div>
        </SubBar>
        <MainView.Content grayBackground hasSubBar padded={false}>
          <MainView.Dimmer loading={fetchLoading && !fetchDone} failed={fetchFailed} retry={fetchSlots} />
          <div styleName="calendar-container">
            <SlotsCalendar
              loading={fetchLoading && fetchDone}
              withAgenda
              slots={fetchDone ? filteredSlots : []}
              modifiers={calendarModifiers}
              onSlotSelect={bind(this.handleSlotSelect, this)}
              onNavigate={bind(this.handleCalendarNavigate, this)}
            />
          </div>
        </MainView.Content>
      </MainView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/calendar/CalendarView/CalendarView.jsx